import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Download, UsbOutlined } from '@mui/icons-material';
import { ParkingAgentService, UsbKit } from 'services/ParkingAgentService';
import UsbSiteCodeControl from './UsbSiteCodeControl';
import { useAgentRun } from './useAgentRun';
import { canWriteToUsb, writeKitToUsb, UsbWriteProgress } from './usbDirectWrite';

interface UsbKitPanelProps {
  projectId: number;
}

/** 1KB 단위가 아니라 사람이 USB 용량과 바로 비교할 수 있는 표기. */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

const UsbKitPanel: React.FC<UsbKitPanelProps> = ({ projectId }) => {
  const { run, history, loading, busy, error, start, select } = useAgentRun<UsbKit>(
    projectId,
    'usb_kit',
  );
  const [siteReady, setSiteReady] = useState(false);
  // USB 직접 쓰기 — 내려받기·압축풀기·write-usb.sh 를 대신한다.
  const [usbProgress, setUsbProgress] = useState<UsbWriteProgress | null>(null);
  const [usbWriting, setUsbWriting] = useState(false);
  const [usbDone, setUsbDone] = useState<{ folder: string; files: number } | null>(null);
  const [usbError, setUsbError] = useState('');
  const usbSupported = canWriteToUsb();

  // 이미지·deb·OS 도구는 공통 설치 킷 몫이라 현장 패키지에는 고를 것이 없다.
  const build = () => start({ kind: 'usb_kit' });

  const kit = run?.status === 'completed' ? run.result : undefined;

  const writeToUsb = async () => {
    if (!kit || !run) return;
    setUsbError('');
    setUsbDone(null);
    setUsbProgress(null);
    setUsbWriting(true);
    try {
      const result = await writeKitToUsb(
        ParkingAgentService.usbKitDownloadUrl(projectId, run.id),
        `luzer-${kit.site_code}`,
        setUsbProgress,
      );
      // null = 사용자가 폴더 선택을 취소했다. 오류가 아니므로 조용히 끝낸다.
      if (result) setUsbDone(result);
    } catch (e) {
      setUsbError(e instanceof Error ? e.message : 'USB 에 쓰지 못했습니다.');
    } finally {
      setUsbWriting(false);
      setUsbProgress(null);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6">현장 패키지 만들기</Typography>
          <Typography variant="body2" color="text.secondary">
            이 현장의 현장 코드·라우터/조명 설정·맵·ROI·장비 목록만 묶습니다(수 MB). 설치 프로그램과
            도커 이미지는 위 〈공통 설치 킷〉에 있으니, USB 에 둘을 나란히 담으면 현장에서 이
            현장을 골라 설치합니다.
            {usbSupported
              ? ' 패키지를 만든 뒤 [USB에 바로 쓰기] 를 누르고 꽂아 둔 USB 를 한 번 고르면 luzer-현장코드 폴더로 세팅됩니다.'
              : ' 이 브라우저는 USB 직접 쓰기를 지원하지 않습니다(Chrome 또는 Edge 에서 열면 버튼 하나로 끝납니다). 지금은 패키지를 내려받아 안의 write-usb.sh 로 USB 에 복사하세요.'}
          </Typography>
        </Box>

        <UsbSiteCodeControl
          key={projectId}
          projectId={projectId}
          busy={busy}
          onReady={setSiteReady}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="contained"
            startIcon={<UsbOutlined />}
            disabled={busy || loading || !siteReady}
            onClick={() => {
              void build();
            }}
            sx={{ minHeight: 44 }}
          >
            {busy ? '패키지 만드는 중…' : '현장 패키지 만들기'}
          </Button>
          {kit && run && usbSupported && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<UsbOutlined />}
              disabled={usbWriting || busy}
              onClick={() => {
                void writeToUsb();
              }}
              sx={{ minHeight: 44 }}
            >
              {usbWriting ? 'USB 에 쓰는 중…' : 'USB 에 바로 쓰기'}
            </Button>
          )}
          {kit && run && (
            <Button
              variant="outlined"
              startIcon={<Download />}
              href={ParkingAgentService.usbKitDownloadUrl(projectId, run.id)}
              sx={{ minHeight: 44 }}
            >
              내려받기 ({formatBytes(kit.bytes)})
            </Button>
          )}
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}
        {usbError && <Alert severity="error">{usbError}</Alert>}

        {usbWriting && (
          <Box role="status">
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-all' }}>
              {usbProgress
                ? `USB 에 쓰는 중 — ${usbProgress.files}개 (${formatBytes(usbProgress.bytes)}) · ${usbProgress.path}`
                : 'USB 폴더를 고르면 바로 시작합니다. 창을 닫지 마세요.'}
            </Typography>
          </Box>
        )}

        {usbDone && (
          <Alert severity="success">
            USB 세팅 완료 — <code>{usbDone.folder}</code> 에 {usbDone.files}개 파일을 썼습니다. USB 를
            빼서 현장 통합서버에 꽂으면 됩니다.
          </Alert>
        )}

        {(busy || loading) && (
          <Box role="status">
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>
              {busy
                ? '서버에서 현장 패키지를 만들고 있습니다. 화면을 나갔다 돌아와도 결과가 유지됩니다.'
                : '저장된 패키지 정보를 불러오고 있습니다.'}
            </Typography>
          </Box>
        )}

        {history.length > 1 && (
          <TextField
            select
            label="제작 이력"
            size="small"
            value={run?.id || ''}
            disabled={busy || loading}
            onChange={(e) => void select(e.target.value)}
          >
            {history.map((item) => (
              <MenuItem value={item.id} key={item.id}>
                {new Date(item.created_at).toLocaleString('ko-KR')}
              </MenuItem>
            ))}
          </TextField>
        )}

        {run?.status === 'failed' && (
          <Alert severity="error">
            {run.error || '패키지를 만들지 못했습니다. 다시 시도해주세요.'}
          </Alert>
        )}

        {kit && (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`현장 코드 ${kit.site_code}`} size="small" />
              <Chip label={kit.filename} size="small" variant="outlined" />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              SHA256 {kit.sha256}
            </Typography>
            {kit.warnings.length > 0 && (
              <Alert severity="warning">
                <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                  {kit.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </Stack>
              </Alert>
            )}
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>패키지 내용</TableCell>
                    <TableCell align="right">크기</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {kit.entries.map((entry) => (
                    <TableRow key={entry.path}>
                      <TableCell sx={{ wordBreak: 'break-all' }}>{entry.path}</TableCell>
                      <TableCell align="right">{formatBytes(entry.bytes)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {usbSupported
                ? 'USB 에 쓴 뒤 현장에서 할 일은 패키지 안 INSTALL.md 에 정리되어 있습니다.'
                : 'USB 에 담는 방법은 패키지 안 INSTALL.md 에 정리되어 있습니다.'}
            </Typography>
          </Stack>
        )}

        {!run && !loading && (
          <Typography color="text.secondary">
            아직 만든 현장 패키지가 없습니다. 현장 코드가 지정된 현장에서만 만들 수 있습니다.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

export default UsbKitPanel;
